// Phase 41 Plan 41-08 — Surface 5d-A SLA Task timer dual-bar primitive.
//
// Renders two side-by-side horizontal bars (R = Response, X = Resolution)
// with:
//   - width proportional to pct (capped at 100% even on breach)
//   - tone by pct (OK emerald < 70 / at-risk amber < 90 / warning orange < 100 / breach rose)
//   - stopped-state override (gray) when the timer is frozen
//   - paused-state visual (opacity 0.4 + " * paused" label suffix) for TASK-07
//   - threshold tick marks (vertical lines) at warningThresholds[i]% positions for TASK-05
//
// Density:
//   - 'inline' (default) — small font + thin bar; used in kanban TaskCard
//     AND inside the task detail surface per CONTEXT D-4 (5d-A inline dual
//     bars only — uniform density across contexts; NO Big timer panel).
//   - 'detail' — bigger font + thicker bar; reserved for any future
//     surface that wants more presence (e.g. an SLA-tier overview page).
//
// English-only labels per D-Copy gate.
//
// Wire-shape note: this primitive accepts a `SLATimerBarInput` shape that
// is ALWAYS the display-time view. The wrapper `SlaTaskCard.tsx` adapts
// the backend `SlaTaskTimerSnapshot` (ms-based + minute targets) into
// this view. Threshold ticks + paused state are first-class here so the
// primitive is ready when Phase 41 milestone-end backlog adds the
// backend fields (see 41-08-SUMMARY Pitfall 3 Backlog).

export type SlaTimerEffect =
  | 'running'
  | 'response_stopped'
  | 'waiting'
  | 'resolved'
  | 'terminal';

export interface SLATimerBarInput {
  /** Pre-computed percentage for the response milestone (0 = none accrued). */
  responsePct: number;
  /** Elapsed seconds for the response milestone. */
  responseElapsedSec: number;
  /** Tier-configured response target in seconds. */
  responseTargetSec: number;
  /** True once the response window has stopped accruing (frozen at first response_stopped transition). */
  responseStopped: boolean;
  /** Pre-computed percentage for the resolution milestone. */
  resolutionPct: number;
  /** Elapsed seconds for the resolution milestone. */
  resolutionElapsedSec: number;
  /** Tier-configured resolution target in seconds. */
  resolutionTargetSec: number;
  /** True once the resolution window has stopped accruing (terminal state). */
  resolutionStopped: boolean;
  /** Current state effect — drives the paused visual when 'waiting'. Null when unknown. */
  currentStateEffect: SlaTimerEffect | null;
  /** Per-tier warning threshold percentages (e.g. [50, 80]) — rendered as vertical tick marks. */
  warningThresholds: number[];
}

export interface SLATimerBarProps {
  timer: SLATimerBarInput;
  density?: 'inline' | 'detail';
}

function fmtDuration(sec: number): string {
  const safeSec = sec < 0 ? 0 : Math.floor(sec);
  const h = Math.floor(safeSec / 3600);
  const m = Math.floor((safeSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function barTone(pct: number): string {
  if (pct >= 100) return '#F43F5E'; // breach rose
  if (pct >= 90) return '#F97316'; // warning orange
  if (pct >= 70) return '#F59E0B'; // at-risk amber
  return '#10B981'; // OK emerald
}

function isPaused(effect: SlaTimerEffect | null): boolean {
  return effect === 'waiting';
}

interface SingleBarProps {
  label: 'R' | 'X';
  pct: number;
  elapsedSec: number;
  targetSec: number;
  stopped: boolean;
  paused: boolean;
  thresholds: number[];
  density: 'inline' | 'detail';
}

function SingleBar({
  label,
  pct,
  elapsedSec,
  targetSec,
  stopped,
  paused,
  thresholds,
  density,
}: SingleBarProps) {
  const tone = stopped ? '#A0A0A8' : barTone(pct);
  const heightCls = density === 'detail' ? 'h-2.5' : 'h-1.5';
  const fontCls = density === 'detail' ? 'text-[11px]' : 'text-[9.5px]';
  return (
    <div className="flex-1 min-w-0">
      <div className={`flex justify-between ${fontCls} text-ms-text-muted mb-0.5`}>
        <span className="font-mono font-bold">{label}</span>
        <span className="tabular-nums">
          {fmtDuration(elapsedSec)} / {fmtDuration(targetSec)}
          {paused && ' * paused'}
        </span>
      </div>
      <div
        className={`${heightCls} bg-ms-surface-2 rounded-full relative overflow-hidden`}
        data-testid={`sla-timer-bar-${label}`}
      >
        <div
          data-testid={`sla-timer-fill-${label}`}
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, pct)}%`,
            background: tone,
            opacity: paused ? 0.4 : 1,
          }}
        />
        {thresholds.map((t) => (
          <span
            key={t}
            data-testid={`sla-timer-threshold-${label}-${t}`}
            className="absolute top-0 bottom-0 w-px bg-ms-text-strong/40"
            style={{ left: `${Math.min(100, t)}%` }}
            title={`${t}% threshold`}
          />
        ))}
      </div>
    </div>
  );
}

export function SLATimerBar({ timer, density = 'inline' }: SLATimerBarProps) {
  const paused = isPaused(timer.currentStateEffect);
  return (
    <div className="flex gap-3 w-full">
      <SingleBar
        label="R"
        pct={timer.responsePct}
        elapsedSec={timer.responseElapsedSec}
        targetSec={timer.responseTargetSec}
        stopped={timer.responseStopped}
        paused={paused}
        thresholds={timer.warningThresholds}
        density={density}
      />
      <SingleBar
        label="X"
        pct={timer.resolutionPct}
        elapsedSec={timer.resolutionElapsedSec}
        targetSec={timer.resolutionTargetSec}
        stopped={timer.resolutionStopped}
        paused={paused}
        thresholds={timer.warningThresholds}
        density={density}
      />
    </div>
  );
}
